import { ROUTES } from "./routes";

/** Preferred origin for indexing (www). Override via REACT_APP_SITE_URL if needed. */
export const SITE_ORIGIN = (
  process.env.REACT_APP_SITE_URL || "https://www.totties.co.za"
).replace(/\/$/, "");

export const SITE_NAME = "Totties";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/logo512.png`;
export const TWITTER_HANDLE = "@totties"; // update if you have a real handle

export type PageSeo = {
  title: string;
  description: string;
  canonical: string;
  robots: string;
  keywords: string;
  ogType: "website" | "article";
  /** Path for og:image if different from default */
  image?: string;
};

export const INDEXABLE_PATHS: string[] = [
  ROUTES.home,
  ROUTES.carAffordability,
  ROUTES.homeAffordability,
  ROUTES.bondRepayment,
  ROUTES.tax,
  ROUTES.monthlyFinances,
  ROUTES.about,
];

export function canonicalUrl(pathname: string): string {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (!normalized || normalized === "/") {
    return `${SITE_ORIGIN}/`;
  }
  return `${SITE_ORIGIN}${normalized}`;
}

type PageMeta = Omit<PageSeo, "canonical" | "robots" | "ogType" | "image"> & {
  ogType?: PageSeo["ogType"];
};

const PAGE_SEO: Record<string, PageMeta> = {
  [ROUTES.home]: {
    title: "Totties – Free South African Tax, Bond & Affordability Calculators",
    description:
      "Free online calculators for SARS income tax, car and home affordability, bond repayments, and monthly budgets. Built for South Africans — no sign-up required.",
    keywords:
      "South African tax calculator, bond calculator, car affordability, home loan calculator, monthly budget, SARS tax, UIF, take home pay, financial tools South Africa",
    ogType: "website",
  },
  [ROUTES.carAffordability]: {
    title: "Car Affordability Calculator South Africa | Totties",
    description:
      "Calculate car finance repayments, total interest, and affordability vs your income. Free vehicle loan calculator with balloon payment support for South Africa.",
    keywords:
      "car affordability calculator, car finance calculator South Africa, vehicle loan repayment, car instalment calculator, balloon payment calculator",
  },
  [ROUTES.homeAffordability]: {
    title: "Home Affordability Calculator South Africa | Totties",
    description:
      "Estimate the maximum home price and bond amount you can afford based on gross income, deposit, interest rate, and loan term. Free home loan affordability tool.",
    keywords:
      "home affordability calculator, how much house can I afford South Africa, bond affordability, home loan calculator, property affordability",
  },
  [ROUTES.bondRepayment]: {
    title: "Bond Repayment Calculator South Africa | Totties",
    description:
      "Calculate monthly home loan repayments, total interest, and savings from extra payments, lump sums, or rate changes. Free South African bond calculator.",
    keywords:
      "bond repayment calculator, home loan calculator South Africa, mortgage calculator, extra bond payments, lump sum bond payment, interest rate change",
  },
  [ROUTES.tax]: {
    title: "SARS Income Tax Calculator 2025/2026 | Totties",
    description:
      "Estimate South African income tax, UIF, and take-home pay for the 2025/2026 tax year. Supports multiple income streams, age rebates, and Excel export.",
    keywords:
      "SARS tax calculator, income tax calculator South Africa, PAYE calculator, take home pay, UIF calculator, tax year 2025 2026",
  },
  [ROUTES.monthlyFinances]: {
    title: "Monthly Budget Planner South Africa | Totties",
    description:
      "Track monthly income and expenses for one or more people. Split shared costs, see remaining budget, and export your plan. Free household budget tool.",
    keywords:
      "monthly budget planner, household budget South Africa, expense tracker, shared expenses calculator, personal finance budget",
  },
  [ROUTES.about]: {
    title: "About Totties – Free SA Financial Calculators",
    description:
      "Totties helps South Africans make clearer money decisions with free, accurate calculators for tax, bonds, affordability, and monthly budgets.",
    keywords:
      "about Totties, South African financial tools, free money calculators",
  },
};

const DEFAULT_SEO: PageMeta = {
  title: "Totties – Money toolkit for South Africans",
  description:
    "Free financial calculators for South Africans — tax, affordability, bonds, and monthly budgets.",
  keywords: "Totties, South Africa, financial calculator",
};

export function getSeoForPath(pathname: string): PageSeo {
  const canonical = canonicalUrl(pathname);

  if (pathname === ROUTES.callback) {
    return {
      title: "Signing in | Totties",
      description: DEFAULT_SEO.description,
      canonical,
      robots: "noindex, nofollow",
      keywords: "",
      ogType: "website",
    };
  }

  const page = PAGE_SEO[pathname] ?? DEFAULT_SEO;
  const indexable = INDEXABLE_PATHS.includes(pathname);

  return {
    title: page.title,
    description: page.description,
    keywords: page.keywords,
    canonical,
    robots: indexable ? "index, follow, max-image-preview:large" : "noindex, nofollow",
    ogType: page.ogType ?? "website",
  };
}

export type FaqItem = { question: string; answer: string };

export const PAGE_FAQS: Partial<Record<string, FaqItem[]>> = {
  [ROUTES.carAffordability]: [
    {
      question: "How is car affordability calculated?",
      answer:
        "Enter the purchase price, deposit, interest rate, and loan term. If you add monthly net income, Totties compares your instalment to income and shows an affordability ratio (under 30% is generally comfortable).",
    },
    {
      question: "Can I include a balloon payment?",
      answer:
        "Yes. Add a residual or balloon amount to model finance structures where you pay a lump sum at the end of the term.",
    },
  ],
  [ROUTES.homeAffordability]: [
    {
      question: "How much home can I afford in South Africa?",
      answer:
        "The calculator uses your combined gross monthly income and a standard debt-to-income guideline to estimate maximum loan size, monthly repayment, and total affordable property price including your deposit.",
    },
    {
      question: "Can I add a co-applicant's income?",
      answer:
        "Yes. Enter a second gross monthly income for joint bonds or co-ownership — both incomes are combined in the calculation.",
    },
  ],
  [ROUTES.bondRepayment]: [
    {
      question: "How do extra bond payments affect my loan?",
      answer:
        "Additional monthly payments or once-off lump sums reduce the outstanding balance faster, which lowers total interest and can shorten the loan term.",
    },
    {
      question: "Can I model interest rate changes?",
      answer:
        "Yes. Add rate change events by month to see how increases or decreases affect your repayment schedule over time.",
    },
  ],
  [ROUTES.tax]: [
    {
      question: "Which tax year does this calculator use?",
      answer:
        "Totties uses the South African 2025/2026 tax year (1 March 2025 – 28 February 2026), including current SARS brackets, rebates, and UIF.",
    },
    {
      question: "Can I calculate tax on multiple income sources?",
      answer:
        "Yes. Add salary, freelance, rental, or other taxable income streams and see combined tax, effective rate, and take-home pay.",
    },
  ],
  [ROUTES.monthlyFinances]: [
    {
      question: "Can I track a shared household budget?",
      answer:
        "Yes. Choose two or more people, split shared expenses by percentage, and see each person's remaining budget and settlement amounts.",
    },
    {
      question: "Can I export my budget?",
      answer:
        "You can generate a PDF summary of your monthly finances from the results section when you have entered income and expenses.",
    },
  ],
};

export function getBreadcrumbJsonLd(pathname: string): object | null {
  if (!INDEXABLE_PATHS.includes(pathname) || pathname === ROUTES.home) {
    return null;
  }

  const labels: Record<string, string> = {
    [ROUTES.carAffordability]: "Car Affordability",
    [ROUTES.homeAffordability]: "Home Affordability",
    [ROUTES.bondRepayment]: "Bond Repayment",
    [ROUTES.tax]: "Tax Calculator",
    [ROUTES.monthlyFinances]: "Monthly Finances",
    [ROUTES.about]: "About",
  };

  const name = labels[pathname];
  if (!name) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_ORIGIN}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name,
        item: canonicalUrl(pathname),
      },
    ],
  };
}

export function getFaqJsonLd(pathname: string): object | null {
  const faqs = PAGE_FAQS[pathname];
  if (!faqs?.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function getWebApplicationJsonLd(pathname: string): object | null {
  if (!INDEXABLE_PATHS.includes(pathname) || pathname === ROUTES.about) {
    return null;
  }

  const seo = getSeoForPath(pathname);
  const calculatorPaths: string[] = [
    ROUTES.carAffordability,
    ROUTES.homeAffordability,
    ROUTES.bondRepayment,
    ROUTES.tax,
    ROUTES.monthlyFinances,
  ];
  if (!calculatorPaths.includes(pathname)) return null;

  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: seo.title.replace(/ \| Totties$/, ""),
    description: seo.description,
    url: seo.canonical,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "ZAR",
    },
    inLanguage: "en-ZA",
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
  };
}

export function getOrganizationJsonLd(): object {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: DEFAULT_OG_IMAGE,
    description:
      "Free financial calculators for South Africans — tax, bonds, affordability, and budgets.",
    areaServed: {
      "@type": "Country",
      name: "South Africa",
    },
  };
}

export function getWebsiteJsonLd(): object {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    description: DEFAULT_SEO.description,
    inLanguage: "en-ZA",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_ORIGIN}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function getJsonLdForPath(pathname: string): object[] {
  const schemas: object[] = [];

  if (pathname === ROUTES.home) {
    schemas.push(getOrganizationJsonLd(), getWebsiteJsonLd());
  }

  const breadcrumb = getBreadcrumbJsonLd(pathname);
  if (breadcrumb) schemas.push(breadcrumb);

  const faq = getFaqJsonLd(pathname);
  if (faq) schemas.push(faq);

  const webApp = getWebApplicationJsonLd(pathname);
  if (webApp) schemas.push(webApp);

  return schemas;
}
